// WebSocket service as specified in appmark.txt
import { WebSocketMessage } from "../types";

type MessageHandler = (message: WebSocketMessage) => void;
type ConnectionHandler = (connected: boolean) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeouts = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff as specified
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isConnected = false;
  private wsUrl: string;

  constructor() {
    // WebSocket URL configuration
    // In production: connections go through the frontend nginx proxy
    // In development: connect directly to backend API server
    const apiUrl = process.env.REACT_APP_API_URL;

    if (apiUrl) {
      // Explicit API URL configured (production or custom setup)
      const wsUrl = apiUrl.replace("http://", "ws://").replace("https://", "wss://");
      this.wsUrl = `${wsUrl}/ws/dashboard`;
    } else if (process.env.NODE_ENV === "production") {
      // Production: Use frontend origin (nginx proxy handles routing)
      const frontendUrl = window.location.origin;
      const wsUrl = frontendUrl
        .replace("http://", "ws://")
        .replace("https://", "wss://");
      this.wsUrl = `${wsUrl}/ws/dashboard`;
    } else {
      // Development: Connect directly to backend
      this.wsUrl = "ws://localhost:8000/ws/dashboard";
    }
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    try {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        console.log("WebSocket connected");
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.notifyConnectionHandlers(true);
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.notifyMessageHandlers(message);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };

      this.ws.onclose = () => {
        console.log("WebSocket disconnected");
        this.isConnected = false;
        this.notifyConnectionHandlers(false);
        this.stopHeartbeat();
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.stopHeartbeat();
    this.isConnected = false;
    this.reconnectAttempts = 0;
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      return;
    }

    const timeout = this.reconnectTimeouts[this.reconnectAttempts] || 16000;
    console.log(
      `Reconnecting in ${timeout}ms (attempt ${this.reconnectAttempts + 1})`
    );

    setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, timeout);
  }

  private startHeartbeat(): void {
    // Send ping every 30 seconds as specified in appmark
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private notifyMessageHandlers(message: WebSocketMessage): void {
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error("Error in message handler:", error);
      }
    });
  }

  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach((handler) => {
      try {
        handler(connected);
      } catch (error) {
        console.error("Error in connection handler:", error);
      }
    });
  }

  // Public methods for managing handlers
  addMessageHandler(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  addConnectionHandler(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn("WebSocket not connected, message not sent:", message);
    }
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
export default webSocketService;
